/* eslint-disable react/display-name */
import React from 'react';
import Select from 'react-select';
import AsyncSelect from 'react-select/async';
import { Input } from 'reactstrap';
import classNames from 'classnames';

import dayjs, { dateInputFieldFormat } from '~libs/dayjs';

import { Currency, DateInput } from '~components/form-fields/inputs';

import { Column } from './types';

interface BaseOption {
  id: string | number;
  name: string;
}

/**
 * Single select editor
 */
export function selectEditor(
  options: BaseOption[] | ((row: any, context?: any) => BaseOption[]),
  disabledOnEmpty?: boolean,
  column?: Column,
  isClearable = false,
) {
  return (value: any, row: any, onChange: (val: any) => void, context?: any, disabled = false) => {
    const resolved = typeof options === 'function' ? options(row, context) : options;
    const finalOptions = resolved.map(({ id, name }) => ({ value: id, label: name }));
    const fromFinal = finalOptions.find((opt) => String(opt.value) === String(value));
    const fromOriginal = Array.isArray(options) ? options.find((o) => String(o.id) === String(value)) : undefined;
    const selectedOption = fromFinal ?? (fromOriginal ? { value: fromOriginal.id, label: fromOriginal.name } : null);

    return (
      <Select
        className={classNames(['cell', column?.classNames?.cell])}
        options={finalOptions}
        value={selectedOption}
        onChange={(selected) => onChange(selected?.value ?? null)}
        isDisabled={(disabledOnEmpty && finalOptions.length === 0) || disabled}
        menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
        styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
        isClearable={isClearable}
      />
    );
  };
}

export function selectLazyEditor(
  options: BaseOption[] | ((row: any, context?: any) => BaseOption[]),
  initialValue?: (row: any) => { id: string | number; name: string } | null,
  disabledOnEmpty?: boolean,
) {
  return (value: any, row: any, onChange: (val: any) => void, context?: any, disabled = false) => {
    const resolved = typeof options === 'function' ? options(row, context) : options;
    const finalOptions = resolved.map(({ id, name }: any) => ({ value: id, label: name }));

    let selectedOption = finalOptions.find((option: any) => option.value === value);

    if (!selectedOption && value && initialValue) {
      const initialRowValue = initialValue(row);
      if (initialRowValue) {
        selectedOption = { value: initialRowValue.id, label: initialRowValue.name };
      }
    }

    const loadOptions = async (inputValue: string) => {
      return finalOptions.filter((option: any) => option.label?.toLowerCase().includes(inputValue.toLowerCase()));
    };

    return (
      <AsyncSelect
        className="cell cell-align-left"
        loadOptions={loadOptions}
        defaultOptions={finalOptions}
        cacheOptions
        value={selectedOption ?? null}
        onChange={(selected: any) => onChange(selected?.value ?? null)}
        isDisabled={(disabledOnEmpty && finalOptions.length === 0) || disabled}
        menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
        styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
      />
    );
  };
}

/**
 * Multi-select editor
 */
export function multiSelectEditor(
  options: BaseOption[] | ((row: any, context?: any) => BaseOption[]),
  disabledOnEmpty?: boolean,
  column?: Column,
) {
  return (value: any, row: any, onChange: (val: any) => void, context?: any, disabled = false) => {
    const resolved = typeof options === 'function' ? options(row, context) : options;
    const finalOptions = resolved.map(({ id, name }) => ({ value: id, label: name }));

    const selectedValues = Array.isArray(value) ? value : [];
    const selectedSet = new Set(selectedValues.map(String));
    let selectedOptions = finalOptions.filter(({ value }) => selectedSet.has(String(value)));
    if (Array.isArray(options)) {
      const missing = options
        .filter((o) => selectedSet.has(String(o.id)) && !selectedOptions.some((s) => String(s.value) === String(o.id)))
        .map((o) => ({ value: o.id, label: o.name }));
      selectedOptions = selectedOptions.concat(missing);
    }

    return (
      <Select
        isMulti
        closeMenuOnSelect={false}
        className={classNames(['cell', column?.classNames?.cell])}
        isClearable={false}
        options={finalOptions}
        value={selectedOptions}
        onChange={(selected) => onChange((selected ?? []).map(({ value }) => value))}
        isDisabled={(disabledOnEmpty && finalOptions.length === 0) || disabled}
        menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
        styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
      />
    );
  };
}

/**
 * Checkbox editor
 */
export function checkboxEditor(column?: Column) {
  return (value: any, _row: any, onChange: (val: any) => void, _context?: any, disabled = false) => (
    <Input
      type="checkbox"
      checked={value === true}
      onChange={(e) => onChange(e.target.checked)}
      disabled={disabled}
      className={classNames(['form-check-input', 'cell', column?.classNames?.cell])}
      style={{ width: 25, height: 25 }}
    />
  );
}

/**
 * Number editor with min/max (static or row-based)
 */
export function numberEditor(
  id: string,
  min: number | ((row: any) => number),
  max?: number | ((row: any) => number),
  noDecimals?: boolean,
  column?: Column,
) {
  return (value: any, row: any, onChange: (val: any) => void, _context?: any, disabled = false) => {
    const resolvedMin = typeof min === 'function' ? min(row) : min;
    const resolvedMax = typeof max === 'function' ? max(row) : max;

    return (
      <Input
        id={id}
        name={id}
        type="number"
        min={resolvedMin}
        disabled={disabled}
        className={classNames(['cell', column?.classNames?.cell])}
        {...(typeof resolvedMax === 'number' ? { max: resolvedMax } : {})}
        value={value ?? ''}
        step={noDecimals ? '1' : 'any'}
        onChange={({ target: { value } }) => {
          // Remove non-numeric chars except first decimal point
          const sanitized = value.replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1');
          let val = Number(sanitized);

          if (isNaN(val)) {
            onChange(resolvedMin);
            return;
          }

          if (typeof resolvedMax === 'number' && val > resolvedMax) val = resolvedMax;
          if (val < resolvedMin) val = resolvedMin;
          onChange(val);
        }}
        autoFocus
      />
    );
  };
}

/**
 * Date editor
 */
export function dateEditor(id: string, minDate?: string, maxDate?: string, column?: Column) {
  return (value: any, _row: any, onChange: (val: any) => void, _context?: any, disabled = false) => (
    <DateInput
      id={id}
      name={id}
      type="date"
      disabled={disabled}
      className={classNames(['cell', column?.classNames?.cell])}
      minDate={minDate}
      maxDate={maxDate}
      value={value && dayjs(value).format(dateInputFieldFormat)}
      onChange={(_, value) => onChange(value)}
    />
  );
}

/**
 * Text editor
 */
export function textEditor(id: string, column?: Column) {
  return (value: any, _row: any, onChange: (val: any) => void, _context?: any, disabled = false) => (
    <Input
      id={id}
      name={id}
      type="text"
      disabled={disabled}
      className={classNames(['cell', column?.classNames?.cell])}
      value={value ?? ''}
      onChange={({ target: { value } }) => onChange(value)}
    />
  );
}

/**
 * Textarea editor
 */
export function textAreaEditor(id: string, column?: Column) {
  return (value: any, _row: any, onChange: (val: any) => void, _context?: any, disabled = false) => (
    <Input
      id={id}
      name={id}
      type="textarea"
      rows={1}
      style={column?.style}
      disabled={disabled}
      className={classNames(['cell', column?.classNames?.cell])}
      value={value ?? ''}
      onChange={({ target: { value } }) => onChange(value)}
    />
  );
}

/**
 * Currency editor
 */
export function currencyEditor(id: string, noDecimals: boolean, column?: Column) {
  return (value: any, _row: any, onChange: (val: any) => void, _context?: any, disabled = false) => (
    <Currency
      id={id}
      name={id}
      type="currency"
      disabled={disabled}
      className={classNames(['cell', column?.classNames?.cell])}
      value={value}
      onChange={(_, value) => onChange(value)}
      noDecimals={noDecimals}
    />
  );
}
